import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';

// Every Connected Listings row sets its status through a dropdown: channel rows
// (Airbnb/VRBO/Booking.com) get the full backend vocabulary, the direct-booking
// row just Listed/Paused. The pure mappings behind those dropdowns live here.

export type ListingToggleStatus = 'active' | 'inactive';

// Client-only statuses for the rows the Listings page synthesizes so every
// address always shows Airbnb / VRBO / Booking.com. Neither exists in the
// backend: `needs_setup` means no listing row exists and the client hasn't told
// us whether they have one; `concierge_setup` means they said they don't, so
// TIDY is creating it (recorded as the listing_setup metadata marker).
export const NEEDS_SETUP_STATUS = 'needs_setup';
export const CONCIERGE_SETUP_STATUS = 'concierge_setup';

export function isPlaceholderStatus(status: string): boolean {
  return status === NEEDS_SETUP_STATUS || status === CONCIERGE_SETUP_STATUS;
}

// One glowing-dot colour per status, shared by the dropdowns and the listing
// detail page: on-market teal, hidden grey, temporary amber.
export const STATUS_DOT_COLORS: Record<string, string> = {
  connecting: 'var(--tidy-warning, #FECD4E)',
  listed: '#007E80',
  unlisted: '#8E8E93',
  paused: 'var(--tidy-warning, #FECD4E)',
  [NEEDS_SETUP_STATUS]: '#FFB020',
  [CONCIERGE_SETUP_STATUS]: 'var(--tidy-warning, #FECD4E)',
};

// @param status the backend AddressListing status
// @return the dot colour, defaulting to the grey of an unknown state
export function statusDotColor(status: string): string {
  return STATUS_DOT_COLORS[status] ?? '#8E8E93';
}

// The three states a customer can put a connected listing in — everything the
// backend PUT accepts (ALLOWED_CUSTOMER_STATUS_VALUES). `connecting` is absent
// on purpose: it isn't settable, only observed.
export const CHANNEL_STATUS_ITEMS: SelectModel[] = [
  { value: 'listed', viewValue: 'Listed', dotColor: STATUS_DOT_COLORS['listed'] },
  { value: 'unlisted', viewValue: 'Unlisted', dotColor: STATUS_DOT_COLORS['unlisted'] },
  { value: 'paused', viewValue: 'Paused', dotColor: STATUS_DOT_COLORS['paused'] },
];

// The direct booking site has no "hidden but reachable by link" state — it's
// either taking bookings or it isn't — so its dropdown offers only these two.
export const DIRECT_BOOKING_STATUS_ITEMS: SelectModel[] = [
  { value: 'listed', viewValue: 'Listed', dotColor: STATUS_DOT_COLORS['listed'] },
  { value: 'paused', viewValue: 'Paused', dotColor: STATUS_DOT_COLORS['paused'] },
];

// The direct-booking dropdown's two positions: a site that isn't live — whether
// paused now or unlisted by the retired toggle — reads as Paused.
//
// @param status the backend AddressListing status
// @return the dropdown value to display
export function directBookingSelectValue(status: string): 'listed' | 'paused' {
  return status === 'listed' ? 'listed' : 'paused';
}

// How each backend status reads to the customer — shown under the channel name
// in place of the listing ID. Wording mirrors the backend model's own docs.
const CHANNEL_STATUS_DESCRIPTIONS: Record<string, string> = {
  connecting: 'Connection in progress',
  listed: 'Publicly visible and bookable',
  unlisted: 'Hidden from search but accessible via direct link',
  paused: 'Temporarily unavailable for bookings',
  [NEEDS_SETUP_STATUS]: 'Tell us whether you already have this listing',
  [CONCIERGE_SETUP_STATUS]: 'TIDY is creating this listing for you',
};

// @param status the backend AddressListing status
// @return the customer-facing description, or '' for a status we don't know
export function describeChannelStatus(status: string): string {
  return CHANNEL_STATUS_DESCRIPTIONS[status] ?? '';
}

// The on-market/off-market read that drives the row dimming. `unlisted`,
// `paused`, `connecting` — anything but `listed` — reads as off.
//
// @param status the backend AddressListing status
// @return the binary state
export function backendStatusToToggle(status: string): ListingToggleStatus {
  return status === 'listed' ? 'active' : 'inactive';
}
